import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Trash2,
  ArrowLeft,
  FileText,
  BarChart3,
  Save,
  Eye,
  Users,
  Plus,
  Palette,
  Image,
  Type,
  MessageCircle,
  Upload,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState("stats");
  const [quotes, setQuotes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [designers, setDesigners] = useState([]);
  const [production, setProduction] = useState({});
  const [sliderImages, setSliderImages] = useState([]);
  const [chatSessions, setChatSessions] = useState([]);
  const [loginLogs, setLoginLogs] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [uploadingSlider, setUploadingSlider] = useState(false);
  const [uploadingImage, setUploadingImage] = useState({});
  const sliderFileRef = useRef(null);
  const imageRefs = useRef({});
  const [siteContent, setSiteContent] = useState({
    // Ana Sayfa
    banner_text: "",
    banner_subtext: "",
    hero_title: "",
    hero_highlight: "",
    hero_description: "",
    about_title: "",
    about_text: "",
    stats_label_1: "",
    stats_label_2: "",
    stats_label_3: "",
    // Hizmetler
    services_title: "",
    services_highlight: "",
    services_description: "",
    services_hero_image: "",
    service1_title: "",
    service1_desc: "",
    service1_image: "",
    service2_title: "",
    service2_desc: "",
    service2_image: "",
    service3_title: "",
    service3_desc: "",
    service3_image: "",
    service4_title: "",
    service4_desc: "",
    service4_image: "",
    // Teknoloji
    tech_title: "",
    tech_highlight: "",
    tech_description: "",
    tech_hero_image: "",
    tech1_title: "",
    tech1_desc: "",
    tech2_title: "",
    tech2_desc: "",
    tech3_title: "",
    tech3_desc: "",
    tech4_title: "",
    tech4_desc: "",
    // İletişim
    contact_title: "",
    contact_description: "",
    contact_hero_image: "",
    contact_phone: "",
    contact_email: "",
    contact_address: "",
    contact_hours: ""
  });
  const [savingContent, setSavingContent] = useState(false);
  const [contentTab, setContentTab] = useState("homepage"); // homepage, services, technology, contact
  const [stats, setStats] = useState({
    current_jobs: 0,
    monthly_jobs: 0,
    total_clinics: 0
  });
  const [loading, setLoading] = useState(true);
  const [savingStats, setSavingStats] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [newEmployee, setNewEmployee] = useState({ name: "", position: "", phone: "" });
  const navigate = useNavigate();

  const formatLoginDate = (ts) => {
    try { return new Date(ts).toLocaleString('tr-TR'); } catch { return ts; }
  };
  const parseDevice = (ua = "") => {
    const mobile = /Mobile|Android|iPhone|iPad/i.test(ua);
    let browser = "Tarayıcı";
    if (/Edg/i.test(ua)) browser = "Edge";
    else if (/OPR|Opera/i.test(ua)) browser = "Opera";
    else if (/Chrome/i.test(ua)) browser = "Chrome";
    else if (/Firefox/i.test(ua)) browser = "Firefox";
    else if (/Safari/i.test(ua)) browser = "Safari";
    return `${mobile ? "Mobil" : "Bilgisayar"} · ${browser}`;
  };

  const handleClearLogs = async () => {
    if (!window.confirm("Tüm giriş kayıtları silinsin mi?")) return;
    const token = localStorage.getItem("admin_token");
    try {
      await axios.delete(`${API}/admin/login-logs`, { headers: { Authorization: `Bearer ${token}` } });
      setLoginLogs([]);
      toast.success("Giriş kayıtları temizlendi");
    } catch (e) {
      toast.error("Kayıtlar temizlenemedi");
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    
    if (!token) {
      navigate("/admin");
      return;
    }
    verifyToken(token);
  }, [navigate]);

  const verifyToken = async (token) => {
    try {
      await axios.get(`${API}/admin/verify`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchData();
    } catch (error) {
      localStorage.removeItem("admin_token");
      navigate("/admin");
    }
  };

  const fetchData = async () => {
    const token = localStorage.getItem("admin_token");
    try {
      const [quotesRes, statsRes, employeesRes, designersRes, productionRes, sliderRes, contentRes, chatRes, loginRes] = await Promise.all([
        axios.get(`${API}/quotes`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/stats`),
        axios.get(`${API}/employees`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/designers`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/production`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/slider`),
        axios.get(`${API}/content`),
        axios.get(`${API}/chat-sessions`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/admin/login-logs`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setQuotes(quotesRes.data);
      setStats(statsRes.data);
      setEmployees(employeesRes.data);
      setDesigners(designersRes.data);
      setSliderImages(sliderRes.data);
      setSiteContent(contentRes.data);
      setChatSessions(chatRes.data);
      setLoginLogs(loginRes.data);
      
      // Convert production array to object for easy lookup
      const prodObj = {};
      productionRes.data.forEach(p => {
        prodObj[p.employee_id] = p.count;
      });
      setProduction(prodObj);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveStats = async () => {
    const token = localStorage.getItem("admin_token");
    setSavingStats(true);

    try {
      await axios.put(`${API}/stats`, stats, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("İstatistikler güncellendi!");
    } catch (error) {
      console.error("Error saving stats:", error);
      toast.error("İstatistikler güncellenirken hata oluştu.");
    } finally {
      setSavingStats(false);
    }
  };

  const handleDeleteQuote = async (quoteId) => {
    const token = localStorage.getItem("admin_token");

    try {
      await axios.delete(`${API}/quotes/${quoteId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Teklif talebi silindi!");
      fetchData();
      setSelectedQuote(null);
    } catch (error) {
      console.error("Error deleting quote:", error);
      toast.error("Silme işlemi başarısız.");
    }
  };

  const handleBack = () => {
    localStorage.removeItem("admin_token");
    navigate("/");
  };

  const handleAddEmployee = async () => {
    if (!newEmployee.name || !newEmployee.position || !newEmployee.phone) {
      toast.error("Lütfen tüm alanları doldurun");
      return;
    }
    
    const token = localStorage.getItem("admin_token");
    try {
      await axios.post(`${API}/employees`, newEmployee, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Çalışan eklendi!");
      setNewEmployee({ name: "", position: "", phone: "" });
      setShowAddEmployee(false);
      fetchData();
    } catch (error) {
      console.error("Error adding employee:", error);
      toast.error("Çalışan eklenirken hata oluştu.");
    }
  };

  const handleDeleteEmployee = async (employeeId) => {
    const token = localStorage.getItem("admin_token");
    try {
      await axios.delete(`${API}/employees/${employeeId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Çalışan silindi!");
      fetchData();
    } catch (error) {
      console.error("Error deleting employee:", error);
      toast.error("Silme işlemi başarısız.");
    }
  };

  const handleUpdateProduction = async (employeeId, count) => {
    const token = localStorage.getItem("admin_token");
    const currentMonth = new Date().toISOString().slice(0, 7); // "2026-03"
    
    try {
      await axios.put(`${API}/production`, 
        { employee_id: employeeId, month: currentMonth, count: parseInt(count) || 0 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProduction(prev => ({ ...prev, [employeeId]: parseInt(count) || 0 }));
      toast.success("Üretim güncellendi!");
    } catch (error) {
      console.error("Error updating production:", error);
      toast.error("Güncelleme başarısız.");
    }
  };

  const handleSliderFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingSlider(true);
    const token = localStorage.getItem("admin_token");
    
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      await axios.post(`${API}/slider/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      
      toast.success("Görsel yüklendi!");
      fetchData();
    } catch (error) {
      console.error("Error uploading slider image:", error);
      toast.error("Görsel yüklenirken hata oluştu.");
    } finally {
      setUploadingSlider(false);
      if (sliderFileRef.current) {
        sliderFileRef.current.value = "";
      }
    }
  };

  const handleDeleteSlider = async (imageId) => {
    const token = localStorage.getItem("admin_token");
    try {
      await axios.delete(`${API}/slider/${imageId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Görsel silindi!");
      fetchData();
    } catch (error) {
      console.error("Error deleting slider image:", error);
      toast.error("Silme işlemi başarısız.");
    }
  };

  const handleImageUpload = async (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingImage(prev => ({ ...prev, [fieldName]: true }));
    const token = localStorage.getItem("admin_token");
    
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      
      setSiteContent(prev => ({ ...prev, [fieldName]: response.data.url }));
      toast.success("Görsel yüklendi!");
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error("Görsel yüklenirken hata oluştu.");
    } finally {
      setUploadingImage(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  const getImageUrl = (url) => {
    if (!url) return "";
    return url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
  };

  const handleDeleteChat = async (sessionId) => {
    const token = localStorage.getItem("admin_token");
    try {
      await axios.delete(`${API}/chat-sessions/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Sohbet silindi!");
      setSelectedChat(null);
      fetchData();
    } catch (error) {
      console.error("Error deleting chat:", error);
      toast.error("Silme işlemi başarısız.");
    }
  };

  const handleSaveContent = async () => {
    const token = localStorage.getItem("admin_token");
    setSavingContent(true);
    
    try {
      await axios.put(`${API}/content`, siteContent, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("İçerikler güncellendi!");
    } catch (error) {
      console.error("Error saving content:", error);
      toast.error("İçerikler güncellenirken hata oluştu.");
    } finally {
      setSavingContent(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
    });
  };

  const formatDateTime = (dateStr) => {
    return new Date(dateStr).toLocaleString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside className="bg-gradient-to-b from-[#0A192F] to-[#0d2341] min-h-screen w-64 p-6 hidden md:block">
        <div className="flex items-center gap-3 mb-10">
          <img 
            src="https://customer-assets.emergentagent.com/job_dental-lab-pro-1/artifacts/996kini1_Gemini_Generated_Image_gsnmeggsnmeggsnm.png" 
            alt="Logo" 
            className="h-12 w-auto"
          />
        </div>

        <nav className="space-y-2">
          <button
            onClick={() => setActiveTab("stats")}
            data-testid="nav-stats"
            className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg transition-all ${
              activeTab === "stats"
                ? "text-white bg-cyan-500/20 border-l-4 border-cyan-400"
                : "text-white/70 hover:text-white hover:bg-white/10"
            }`}
          >
            <BarChart3 className="w-5 h-5" />
            <span>İstatistikler</span>
          </button>
          <button
            onClick={() => setActiveTab("quotes")}
            data-testid="nav-quotes"
            className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg transition-all ${
              activeTab === "quotes"
                ? "text-white bg-cyan-500/20 border-l-4 border-cyan-400"
                : "text-white/70 hover:text-white hover:bg-white/10"
            }`}
          >
            <FileText className="w-5 h-5" />
            <span>Teklif Talepleri</span>
          </button>
          <button
            onClick={() => setActiveTab("employees")}
            data-testid="nav-employees"
            className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg transition-all ${
              activeTab === "employees"
                ? "text-white bg-cyan-500/20 border-l-4 border-cyan-400"
                : "text-white/70 hover:text-white hover:bg-white/10"
            }`}
          >
            <Users className="w-5 h-5" />
            <span>Çalışanlar</span>
          </button>
          <button
            onClick={() => setActiveTab("production")}
            data-testid="nav-production"
            className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg transition-all ${
              activeTab === "production"
                ? "text-white bg-cyan-500/20 border-l-4 border-cyan-400"
                : "text-white/70 hover:text-white hover:bg-white/10"
            }`}
          >
            <Palette className="w-5 h-5" />
            <span>Yapılan Üyeler</span>
          </button>
          <button
            onClick={() => setActiveTab("slider")}
            data-testid="nav-slider"
            className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg transition-all ${
              activeTab === "slider"
                ? "text-white bg-cyan-500/20 border-l-4 border-cyan-400"
                : "text-white/70 hover:text-white hover:bg-white/10"
            }`}
          >
            <Image className="w-5 h-5" />
            <span>Slider Görselleri</span>
          </button>
          <button
            onClick={() => setActiveTab("content")}
            data-testid="nav-content"
            className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg transition-all ${
              activeTab === "content"
                ? "text-white bg-cyan-500/20 border-l-4 border-cyan-400"
                : "text-white/70 hover:text-white hover:bg-white/10"
            }`}
          >
            <Type className="w-5 h-5" />
            <span>Site İçerikleri</span>
          </button>
          <button
            onClick={() => setActiveTab("chats")}
            data-testid="nav-chats"
            className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg transition-all ${
              activeTab === "chats"
                ? "text-white bg-cyan-500/20 border-l-4 border-cyan-400"
                : "text-white/70 hover:text-white hover:bg-white/10"
            }`}
          >
            <MessageCircle className="w-5 h-5" />
            <span>Sohbetler</span>
            {chatSessions.length > 0 && (
              <span className="ml-auto bg-cyan-500 text-white text-xs px-2 py-0.5 rounded-full">
                {chatSessions.length}
              </span>
            )}
          </button>
        </nav>

        <div className="mt-auto pt-10">
          <button
            onClick={handleBack}
            className="flex items-center gap-3 w-full px-4 py-3 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-all"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Geri Dön</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-6 md:p-10">
        {/* Mobile Header */}
        <div className="md:hidden flex items-center justify-between mb-6">
          <img 
            src="https://customer-assets.emergentagent.com/job_dental-lab-pro-1/artifacts/996kini1_Gemini_Generated_Image_gsnmeggsnmeggsnm.png" 
            alt="Logo" 
            className="h-10 w-auto"
          />
          <button
            onClick={handleBack}
            className="text-slate-600"
            data-testid="mobile-back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
        </div>

        {/* Mobile Tabs */}
        <div className="md:hidden flex gap-2 mb-6 overflow-x-auto">
          <button
            onClick={() => setActiveTab("stats")}
            className={`py-2 px-3 rounded-lg font-medium text-sm whitespace-nowrap ${
              activeTab === "stats"
                ? "bg-gradient-to-r from-[#0A192F] to-cyan-500 text-white"
                : "bg-white text-slate-600"
            }`}
          >
            İstatistik
          </button>
          <button
            onClick={() => setActiveTab("quotes")}
            className={`py-2 px-3 rounded-lg font-medium text-sm whitespace-nowrap ${
              activeTab === "quotes"
                ? "bg-gradient-to-r from-[#0A192F] to-cyan-500 text-white"
                : "bg-white text-slate-600"
            }`}
          >
            Talepler
          </button>
          <button
            onClick={() => setActiveTab("employees")}
            className={`py-2 px-3 rounded-lg font-medium text-sm whitespace-nowrap ${
              activeTab === "employees"
                ? "bg-gradient-to-r from-[#0A192F] to-cyan-500 text-white"
                : "bg-white text-slate-600"
            }`}
          >
            Çalışanlar
          </button>
          <button
            onClick={() => setActiveTab("production")}
            className={`py-2 px-3 rounded-lg font-medium text-sm whitespace-nowrap ${
              activeTab === "production"
                ? "bg-gradient-to-r from-[#0A192F] to-cyan-500 text-white"
                : "bg-white text-slate-600"
            }`}
          >
            Üyeler
          </button>
          <button
            onClick={() => setActiveTab("slider")}
            className={`py-2 px-3 rounded-lg font-medium text-sm whitespace-nowrap ${
              activeTab === "slider"
                ? "bg-gradient-to-r from-[#0A192F] to-cyan-500 text-white"
                : "bg-white text-slate-600"
            }`}
          >
            Slider
          </button>
          <button
            onClick={() => setActiveTab("content")}
            className={`py-2 px-3 rounded-lg font-medium text-sm whitespace-nowrap ${
              activeTab === "content"
                ? "bg-gradient-to-r from-[#0A192F] to-cyan-500 text-white"
                : "bg-white text-slate-600"
            }`}
          >
            İçerik
          </button>
          <button
            onClick={() => setActiveTab("chats")}
            className={`py-2 px-3 rounded-lg font-medium text-sm whitespace-nowrap ${
              activeTab === "chats"
                ? "bg-gradient-to-r from-[#0A192F] to-cyan-500 text-white"
                : "bg-white text-slate-600"
            }`}
          >
            Sohbet
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-10 h-10 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : activeTab === "stats" ? (
          <>
            {/* Stats Management */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl font-bold text-[#0A192F]">
                Site İstatistikleri
              </h2>
              <Button
                onClick={handleSaveStats}
                disabled={savingStats}
                className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400"
                data-testid="save-stats-btn"
              >
                {savingStats ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Kaydet
                  </>
                )}
              </Button>
            </div>

            <div className="grid grid-cols-1 gap-6 max-w-md">
              <div className="bg-white rounded-xl p-6 shadow-sm border-2 border-transparent hover:border-cyan-200 transition-colors">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center">
                    <BarChart3 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <Label className="text-slate-500 text-sm">Çalıştığımız Klinikler</Label>
                  </div>
                </div>
                <Input
                  type="number"
                  value={stats.total_clinics}
                  onChange={(e) => setStats({ ...stats, total_clinics: parseInt(e.target.value) || 0 })}
                  className="text-3xl font-bold text-[#0A192F] h-16 text-center"
                  data-testid="input-total-clinics"
                />
              </div>
            </div>

            <p className="text-slate-500 text-sm mt-6">
              Bu istatistikler ana sayfadaki kutularda gösterilir.
            </p>

            <div className="bg-white rounded-xl p-6 shadow-sm mt-8" data-testid="admin-login-logs">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-[#0A192F] flex items-center gap-2">
                  <Users className="w-5 h-5 text-cyan-500" /> Admin Giriş Kayıtları
                </h3>
                {loginLogs.length > 0 && (
                  <Button
                    onClick={handleClearLogs}
                    variant="outline"
                    size="sm"
                    className="text-red-500 border-red-200 hover:bg-red-50"
                    data-testid="clear-logs-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-1" /> Kayıtları Temizle
                  </Button>
                )}
              </div>
              {loginLogs.length === 0 ? (
                <p className="text-slate-500 text-sm">Henüz giriş kaydı yok.</p>
              ) : (
                <>
                  <div className="bg-cyan-50 border border-cyan-100 rounded-lg p-4 mb-4" data-testid="last-login-info">
                    <p className="text-sm text-slate-600">Son Giriş</p>
                    <p className="font-semibold text-[#0A192F]">{formatLoginDate(loginLogs[0].timestamp)}</p>
                    <p className="text-sm text-slate-500">{parseDevice(loginLogs[0].user_agent)} · IP: {loginLogs[0].ip}</p>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {loginLogs.map((log, i) => (
                      <div key={i} className="flex flex-col md:flex-row md:items-center md:justify-between py-2 text-sm gap-1">
                        <span className="text-[#0A192F] font-medium">{formatLoginDate(log.timestamp)}</span>
                        <span className="text-slate-500">{parseDevice(log.user_agent)}</span>
                        <span className="text-slate-400">IP: {log.ip}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </>
        ) : activeTab === "quotes" ? (
          <>
            {/* Quotes Management */}
            <h2 className="font-heading text-2xl font-bold text-[#0A192F] mb-6">
              Teklif Talepleri
            </h2>

            {quotes.length === 0 ? (
              <div className="bg-white rounded-xl p-10 text-center">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Henüz teklif talebi yok.</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="text-left px-6 py-4 text-sm font-medium text-slate-600">Ad</th>
                        <th className="text-left px-6 py-4 text-sm font-medium text-slate-600">Tarih</th>
                        <th className="text-center px-6 py-4 text-sm font-medium text-slate-600">Detay</th>
                        <th className="text-right px-6 py-4 text-sm font-medium text-slate-600">Sil</th>
                      </tr>
                    </thead>
                    <tbody>
                      {quotes.map((quote) => (
                        <tr key={quote.id} className="border-b hover:bg-slate-50" data-testid={`quote-${quote.id}`}>
                          <td className="px-6 py-4 font-medium text-[#0A192F]">{quote.name}</td>
                          <td className="px-6 py-4 text-slate-600">{formatDate(quote.created_at)}</td>
                          <td className="px-6 py-4 text-center">
                            <button
                              onClick={() => setSelectedQuote(quote)}
                              className="text-cyan-500 hover:text-cyan-600 p-2"
                              data-testid={`view-quote-${quote.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <button
                              onClick={() => handleDeleteQuote(quote.id)}
                              className="text-red-500 hover:text-red-600 p-2"
                              data-testid={`delete-quote-${quote.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        ) : activeTab === "employees" ? (
          <>
            {/* Employees Management */}
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-heading text-2xl font-bold text-[#0A192F]">
                Çalışanlar
              </h2>
              <Button
                onClick={() => setShowAddEmployee(true)}
                className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Çalışan Ekle
              </Button>
            </div>

            {/* Add Employee Form */}
            {showAddEmployee && (
              <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm mb-6">
                <h3 className="font-medium text-[#0A192F] mb-4">Yeni Çalışan</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm text-slate-500 mb-1">Ad Soyad</label>
                    <Input
                      value={newEmployee.name}
                      onChange={(e) => setNewEmployee({...newEmployee, name: e.target.value})}
                      placeholder="Ad Soyad"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-500 mb-1">Pozisyon</label>
                    <Input
                      value={newEmployee.position}
                      onChange={(e) => setNewEmployee({...newEmployee, position: e.target.value})}
                      placeholder="Ne için çalışıyor"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-500 mb-1">Telefon</label>
                    <Input
                      value={newEmployee.phone}
                      onChange={(e) => setNewEmployee({...newEmployee, phone: e.target.value})}
                      placeholder="05XX XXX XX XX"
                    />
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button onClick={handleAddEmployee} className="bg-green-500 text-white hover:bg-green-600">
                    Kaydet
                  </Button>
                  <Button variant="outline" onClick={() => {setShowAddEmployee(false); setNewEmployee({name:"", position:"", phone:""});}}>
                    İptal
                  </Button>
                </div>
              </div>
            )}

            {employees.length === 0 ? (
              <div className="bg-white rounded-xl p-10 text-center">
                <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Henüz çalışan eklenmemiş.</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                {/* Desktop Table */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="text-left px-6 py-4 text-sm font-medium text-slate-600">Ad Soyad</th>
                        <th className="text-left px-6 py-4 text-sm font-medium text-slate-600">Pozisyon</th>
                        <th className="text-left px-6 py-4 text-sm font-medium text-slate-600">Telefon</th>
                        <th className="text-right px-6 py-4 text-sm font-medium text-slate-600">Sil</th>
                      </tr>
                    </thead>
                    <tbody>
                      {employees.map((emp) => (
                        <tr key={emp.id} className="border-b hover:bg-slate-50">
                          <td className="px-6 py-4 font-medium text-[#0A192F]">{emp.name}</td>
                          <td className="px-6 py-4 text-slate-600">{emp.position}</td>
                          <td className="px-6 py-4 text-slate-600">{emp.phone}</td>
                          <td className="px-6 py-4 text-right">
                            <button
                              onClick={() => handleDeleteEmployee(emp.id)}
                              className="text-red-500 hover:text-red-600 p-2"
                              data-testid={`delete-employee-${emp.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Mobile Cards */}
                <div className="md:hidden divide-y">
                  {employees.map((emp) => (
                    <div key={emp.id} className="p-4 flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-[#0A192F] truncate">{emp.name}</p>
                        <p className="text-sm text-slate-500 truncate">{emp.position}</p>
                        <p className="text-sm text-cyan-600">{emp.phone}</p>
                      </div>
                      <button
                        onClick={() => handleDeleteEmployee(emp.id)}
                        className="text-red-500 hover:text-red-600 p-2 ml-2"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : activeTab === "production" ? (
          <>
            {/* Production Tracking */}
            <h2 className="font-heading text-2xl font-bold text-[#0A192F] mb-6">
              Yapılan Üyeler - {new Date().toLocaleString('tr-TR', { month: 'long', year: 'numeric' })}
            </h2>

            {designers.length === 0 ? (
              <div className="bg-white rounded-xl p-10 text-center">
                <Palette className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Henüz tasarımcı eklenmemiş.</p>
                <p className="text-sm text-slate-400 mt-2">
                  Çalışanlar bölümünde pozisyon olarak "Tasarımcı" yazın.
                </p>
              </div>
            ) : (
              <div className="grid gap-4">
                {designers.map((designer) => (
                  <div key={designer.id} className="bg-white rounded-xl p-4 md:p-6 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <h3 className="font-semibold text-[#0A192F] text-lg">{designer.name}</h3>
                      <p className="text-slate-500 text-sm">{designer.position}</p>
                      <p className="text-cyan-600 text-sm">{designer.phone}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <label className="text-sm text-slate-600 whitespace-nowrap">Bu Ay:</label>
                      <Input
                        type="number"
                        min="0"
                        value={production[designer.id] || 0}
                        onChange={(e) => setProduction(prev => ({ ...prev, [designer.id]: e.target.value }))}
                        className="w-20 text-center"
                      />
                      <span className="text-slate-500">adet</span>
                      <Button
                        onClick={() => handleUpdateProduction(designer.id, production[designer.id])}
                        size="sm"
                        className="bg-cyan-500 hover:bg-cyan-600 text-white"
                      >
                        <Save className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                
                {/* Total */}
                <div className="bg-gradient-to-r from-[#0A192F] to-cyan-600 rounded-xl p-4 md:p-6 text-white">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">Toplam Üretim</span>
                    <span className="text-2xl font-bold">
                      {Object.values(production).reduce((sum, val) => sum + (parseInt(val) || 0), 0)} adet
                    </span>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : activeTab === "slider" ? (
          <>
            {/* Slider Management */}
            <h2 className="font-heading text-2xl font-bold text-[#0A192F] mb-6">
              Ana Sayfa Slider Görselleri
            </h2>

            {/* Add New Slider Image - File Upload */}
            <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm mb-6">
              <h3 className="font-medium text-[#0A192F] mb-4">Yeni Görsel Yükle</h3>
              <div className="flex flex-col gap-4">
                <input
                  type="file"
                  ref={sliderFileRef}
                  onChange={handleSliderFileUpload}
                  accept="image/*"
                  className="hidden"
                  data-testid="slider-file-input"
                />
                <Button 
                  onClick={() => sliderFileRef.current?.click()}
                  disabled={uploadingSlider}
                  className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white w-full md:w-auto"
                  data-testid="upload-slider-btn"
                >
                  {uploadingSlider ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  {uploadingSlider ? "Yükleniyor..." : "Görsel Seç ve Yükle"}
                </Button>
                <p className="text-sm text-slate-500">
                  Bilgisayarınızdan görsel seçin. Herhangi bir boyutta resim yükleyebilirsiniz, otomatik ayarlanacaktır.
                </p>
              </div>
            </div>

            {sliderImages.length === 0 ? (
              <div className="bg-white rounded-xl p-10 text-center">
                <Image className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Henüz slider görseli eklenmemiş.</p>
                <p className="text-sm text-slate-400 mt-2">
                  Ana sayfada görüntülenecek görselleri buradan ekleyin.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {sliderImages.map((image, index) => (
                  <div key={image.id} className="bg-white rounded-xl shadow-sm overflow-hidden group">
                    <div className="relative aspect-video">
                      <img
                        src={image.image_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${image.image_url}` : image.image_url}
                        alt={`Slider ${index + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = "https://via.placeholder.com/800x400?text=Görsel+Yüklenemedi";
                        }}
                      />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <button
                          onClick={() => handleDeleteSlider(image.id)}
                          className="bg-red-500 hover:bg-red-600 text-white p-3 rounded-full transition-colors"
                          data-testid={`delete-slider-${image.id}`}
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                    <div className="p-3 flex items-center justify-between">
                      <span className="text-sm text-slate-600">Sıra: {index + 1}</span>
                      <button
                        onClick={() => handleDeleteSlider(image.id)}
                        className="text-red-500 hover:text-red-600 p-1 md:hidden"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : activeTab === "content" ? (
          <>
            {/* Site Content Management */}
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-6 gap-4">
              <h2 className="font-heading text-2xl font-bold text-[#0A192F]">
                Site İçerikleri
              </h2>
              <Button
                onClick={handleSaveContent}
                disabled={savingContent}
                className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400"
                data-testid="save-content-btn"
              >
                {savingContent ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Tümünü Kaydet
                  </>
                )}
              </Button>
            </div>

            {/* Content Tabs */}
            <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
              {[
                { id: "homepage", label: "Ana Sayfa" },
                { id: "services", label: "Hizmetler" },
                { id: "technology", label: "Teknoloji" },
                { id: "contact", label: "İletişim" }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setContentTab(tab.id)}
                  className={`py-2 px-4 rounded-lg font-medium text-sm whitespace-nowrap transition-all ${
                    contentTab === tab.id
                      ? "bg-gradient-to-r from-[#0A192F] to-cyan-500 text-white"
                      : "bg-white text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Homepage Content */}
            {contentTab === "homepage" && (
              <div className="space-y-6">
                <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm">
                  <h3 className="font-semibold text-[#0A192F] mb-4">Üst Banner</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-600 text-sm">Ana Başlık</Label>
                      <Input value={siteContent.banner_text} onChange={(e) => setSiteContent({...siteContent, banner_text: e.target.value})} className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-slate-600 text-sm">Alt Yazı</Label>
                      <Input value={siteContent.banner_subtext} onChange={(e) => setSiteContent({...siteContent, banner_subtext: e.target.value})} className="mt-1" />
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm">
                  <h3 className="font-semibold text-[#0A192F] mb-4">Mavi Alan (Hero)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-600 text-sm">Başlık</Label>
                      <Input value={siteContent.hero_title} onChange={(e) => setSiteContent({...siteContent, hero_title: e.target.value})} className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-slate-600 text-sm">Vurgulu Yazı</Label>
                      <Input value={siteContent.hero_highlight} onChange={(e) => setSiteContent({...siteContent, hero_highlight: e.target.value})} className="mt-1" />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-slate-600 text-sm">Açıklama</Label>
                      <textarea value={siteContent.hero_description} onChange={(e) => setSiteContent({...siteContent, hero_description: e.target.value})} className="mt-1 w-full px-3 py-2 border rounded-md min-h-[80px]" />
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm">
                  <h3 className="font-semibold text-[#0A192F] mb-4">İstatistik Etiketi</h3>
                  <div className="grid grid-cols-1 gap-4 max-w-md">
                    <div>
                      <Label className="text-slate-600 text-sm">Klinik İstatistiği Etiketi</Label>
                      <Input value={siteContent.stats_label_3} onChange={(e) => setSiteContent({...siteContent, stats_label_3: e.target.value})} className="mt-1" />
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm">
                  <h3 className="font-semibold text-[#0A192F] mb-4">Hakkımızda</h3>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-slate-600 text-sm">Başlık</Label>
                      <Input value={siteContent.about_title} onChange={(e) => setSiteContent({...siteContent, about_title: e.target.value})} className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-slate-600 text-sm">İçerik</Label>
                      <textarea value={siteContent.about_text} onChange={(e) => setSiteContent({...siteContent, about_text: e.target.value})} className="mt-1 w-full px-3 py-2 border rounded-md min-h-[120px]" />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Services Content */}
            {contentTab === "services" && (
              <div className="space-y-6">
                <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm">
                  <h3 className="font-semibold text-[#0A192F] mb-4">Sayfa Başlığı ve Görseli</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-600 text-sm">Başlık</Label>
                      <Input value={siteContent.services_title} onChange={(e) => setSiteContent({...siteContent, services_title: e.target.value})} className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-slate-600 text-sm">Vurgulu Yazı</Label>
                      <Input value={siteContent.services_highlight} onChange={(e) => setSiteContent({...siteContent, services_highlight: e.target.value})} className="mt-1" />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-slate-600 text-sm">Açıklama</Label>
                      <textarea value={siteContent.services_description} onChange={(e) => setSiteContent({...siteContent, services_description: e.target.value})} className="mt-1 w-full px-3 py-2 border rounded-md min-h-[60px]" />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-slate-600 text-sm">Hero Görseli</Label>
                      <div className="mt-2 flex items-center gap-4">
                        {siteContent.services_hero_image && <img src={getImageUrl(siteContent.services_hero_image)} alt="" className="w-32 h-20 object-cover rounded" />}
                        <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, 'services_hero_image')} className="text-sm" />
                        {uploadingImage.services_hero_image && <div className="w-5 h-5 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />}
                      </div>
                    </div>
                  </div>
                </div>

                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="bg-white rounded-xl p-4 md:p-6 shadow-sm">
                    <h3 className="font-semibold text-[#0A192F] mb-4">Hizmet {i}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-600 text-sm">Başlık</Label>
                        <Input value={siteContent[`service${i}_title`]} onChange={(e) => setSiteContent({...siteContent, [`service${i}_title`]: e.target.value})} className="mt-1" />
                      </div>
                      <div>
                        <Label className="text-slate-600 text-sm">Açıklama</Label>
                        <Input value={siteContent[`service${i}_desc`]} onChange={(e) => setSiteContent({...siteContent, [`service${i}_desc`]: e.target.value})} className="mt-1" />
                      </div>
                      <div className="md:col-span-2">
                        <Label className="text-slate-600 text-sm">Görsel</Label>
                        <div className="mt-2 flex items-center gap-4">
                          {siteContent[`service${i}_image`] && <img src={getImageUrl(siteContent[`service${i}_image`])} alt="" className="w-24 h-16 object-cover rounded" />}
                          <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, `service${i}_image`)} className="text-sm" />
                          {uploadingImage[`service${i}_image`] && <div className="w-5 h-5 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Technology Content */}
            {contentTab === "technology" && (
              <div className="space-y-6">
                <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm">
                  <h3 className="font-semibold text-[#0A192F] mb-4">Sayfa Başlığı ve Görseli</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-600 text-sm">Başlık</Label>
                      <Input value={siteContent.tech_title} onChange={(e) => setSiteContent({...siteContent, tech_title: e.target.value})} className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-slate-600 text-sm">Vurgulu Yazı</Label>
                      <Input value={siteContent.tech_highlight} onChange={(e) => setSiteContent({...siteContent, tech_highlight: e.target.value})} className="mt-1" />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-slate-600 text-sm">Açıklama</Label>
                      <textarea value={siteContent.tech_description} onChange={(e) => setSiteContent({...siteContent, tech_description: e.target.value})} className="mt-1 w-full px-3 py-2 border rounded-md min-h-[60px]" />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-slate-600 text-sm">Hero Görseli</Label>
                      <div className="mt-2 flex items-center gap-4">
                        {siteContent.tech_hero_image && <img src={getImageUrl(siteContent.tech_hero_image)} alt="" className="w-32 h-20 object-cover rounded" />}
                        <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, 'tech_hero_image')} className="text-sm" />
                        {uploadingImage.tech_hero_image && <div className="w-5 h-5 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />}
                      </div>
                    </div>
                  </div>
                </div>

                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="bg-white rounded-xl p-4 md:p-6 shadow-sm">
                    <h3 className="font-semibold text-[#0A192F] mb-4">Teknoloji {i}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-600 text-sm">Başlık</Label>
                        <Input value={siteContent[`tech${i}_title`]} onChange={(e) => setSiteContent({...siteContent, [`tech${i}_title`]: e.target.value})} className="mt-1" />
                      </div>
                      <div>
                        <Label className="text-slate-600 text-sm">Açıklama</Label>
                        <Input value={siteContent[`tech${i}_desc`]} onChange={(e) => setSiteContent({...siteContent, [`tech${i}_desc`]: e.target.value})} className="mt-1" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Contact Content */}
            {contentTab === "contact" && (
              <div className="space-y-6">
                <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm">
                  <h3 className="font-semibold text-[#0A192F] mb-4">Sayfa Başlığı ve Görseli</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-600 text-sm">Başlık</Label>
                      <Input value={siteContent.contact_title} onChange={(e) => setSiteContent({...siteContent, contact_title: e.target.value})} className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-slate-600 text-sm">Açıklama</Label>
                      <Input value={siteContent.contact_description} onChange={(e) => setSiteContent({...siteContent, contact_description: e.target.value})} className="mt-1" />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-slate-600 text-sm">Hero Görseli</Label>
                      <div className="mt-2 flex items-center gap-4">
                        {siteContent.contact_hero_image && <img src={getImageUrl(siteContent.contact_hero_image)} alt="" className="w-32 h-20 object-cover rounded" />}
                        <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, 'contact_hero_image')} className="text-sm" />
                        {uploadingImage.contact_hero_image && <div className="w-5 h-5 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm">
                  <h3 className="font-semibold text-[#0A192F] mb-4">İletişim Bilgileri</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-600 text-sm">Telefon</Label>
                      <Input value={siteContent.contact_phone} onChange={(e) => setSiteContent({...siteContent, contact_phone: e.target.value})} className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-slate-600 text-sm">E-posta</Label>
                      <Input value={siteContent.contact_email} onChange={(e) => setSiteContent({...siteContent, contact_email: e.target.value})} className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-slate-600 text-sm">Adres</Label>
                      <Input value={siteContent.contact_address} onChange={(e) => setSiteContent({...siteContent, contact_address: e.target.value})} className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-slate-600 text-sm">Çalışma Saatleri</Label>
                      <Input value={siteContent.contact_hours} onChange={(e) => setSiteContent({...siteContent, contact_hours: e.target.value})} className="mt-1" />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : activeTab === "chats" ? (
          <>
            {/* Chat Sessions */}
            <h2 className="font-heading text-2xl font-bold text-[#0A192F] mb-6">
              DenHi Sohbetleri
            </h2>

            {chatSessions.length === 0 ? (
              <div className="bg-white rounded-xl p-10 text-center">
                <MessageCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Henüz sohbet kaydı yok.</p>
                <p className="text-sm text-slate-400 mt-2">
                  Ziyaretçiler DenHi ile konuştuğunda burada görünecek.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Chat List */}
                <div className="space-y-3">
                  {chatSessions.map((session) => (
                    <div
                      key={session.session_id}
                      onClick={() => setSelectedChat(session)}
                      className={`bg-white rounded-xl p-4 shadow-sm cursor-pointer transition-all hover:shadow-md ${
                        selectedChat?.session_id === session.session_id ? "ring-2 ring-cyan-500" : ""
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-semibold text-[#0A192F]">
                          {session.user_name || "İsimsiz Kullanıcı"}
                        </h4>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteChat(session.session_id);
                          }}
                          className="text-red-500 hover:text-red-600 p-1"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <p className="text-sm text-slate-500 truncate">
                        {session.messages?.[session.messages.length - 1]?.content || "Mesaj yok"}
                      </p>
                      <p className="text-xs text-slate-400 mt-2">
                        {session.messages?.length || 0} mesaj • {new Date(session.updated_at).toLocaleDateString('tr-TR')}
                      </p>
                    </div>
                  ))}
                </div>

                {/* Chat Detail */}
                {selectedChat && (
                  <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <div className="bg-gradient-to-r from-[#0A192F] to-cyan-600 p-4">
                      <h4 className="text-white font-semibold">
                        {selectedChat.user_name || "İsimsiz Kullanıcı"} ile Sohbet
                      </h4>
                      <p className="text-white/70 text-sm">
                        {selectedChat.messages?.length || 0} mesaj
                      </p>
                    </div>
                    <div className="p-4 max-h-[400px] overflow-y-auto space-y-3">
                      {selectedChat.messages?.map((msg, index) => (
                        <div
                          key={index}
                          className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                        >
                          <div
                            className={`max-w-[80%] px-4 py-2 rounded-2xl ${
                              msg.role === "user"
                                ? "bg-cyan-500 text-white rounded-br-md"
                                : "bg-slate-100 text-slate-700 rounded-bl-md"
                            }`}
                          >
                            <p className="text-sm">{msg.content}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        ) : null}

        {/* Quote Detail Modal */}
        <Dialog open={!!selectedQuote} onOpenChange={() => setSelectedQuote(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Teklif Detayı</DialogTitle>
            </DialogHeader>
            {selectedQuote && (
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Ad Soyad</p>
                    <p className="font-medium text-[#0A192F]">{selectedQuote.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Klinik</p>
                    <p className="font-medium text-[#0A192F]">{selectedQuote.clinic_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Telefon</p>
                    <p className="font-medium text-[#0A192F]">{selectedQuote.phone}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">E-posta</p>
                    <p className="font-medium text-[#0A192F]">{selectedQuote.email}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-sm text-slate-500">Hizmet Türü</p>
                    <p className="font-medium text-[#0A192F]">{selectedQuote.service_type}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-sm text-slate-500">Mesaj</p>
                    <p className="font-medium text-[#0A192F]">{selectedQuote.message}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-sm text-slate-500">Tarih</p>
                    <p className="font-medium text-[#0A192F]">{formatDateTime(selectedQuote.created_at)}</p>
                  </div>
                  {selectedQuote.device_info && (
                    <div className="col-span-2">
                      <p className="text-sm text-slate-500">Cihaz Bilgisi</p>
                      <p className="font-medium text-[#0A192F] text-xs break-all">{selectedQuote.device_info}</p>
                    </div>
                  )}
                </div>
                <Button
                  onClick={() => handleDeleteQuote(selectedQuote.id)}
                  variant="destructive"
                  className="w-full mt-4"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Talebi Sil
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default AdminDashboard;
